import { useEffect, useState } from 'react';
import { Button, Paper, Typography } from '@mui/material';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Place from '@mui/icons-material/Place';
import { useNavigate, useParams } from 'react-router-dom';
import { getEvents } from '../../api/events';
import { eventImageUrl } from '../../api/files';
import type { EventInfo } from '../../api/types';
import { EventStatusChip } from '../../components/EventStatusChip';
import { EmptyState } from '../../components/ui/EmptyState';
import { LoadingScreen } from '../../components/ui/LoadingScreen';
import { formatTime } from '../../utils/format';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useUserData } from '../../hooks/useUserData';

export function EventDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const user = useUserData();

    const [event, setEvent] = useState<EventInfo | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        if (!id) return;
        getEvents({ id })
            .then((list) => {
                if (active) setEvent(list[0] ?? null);
            })
            .catch(() => {
                if (active) setEvent(null);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [id]);

    useDocumentTitle(event ? event.event_title : 'Evento');

    if (loading) return <LoadingScreen />;
    if (!event) return <EmptyState message="Evento non trovato." />;

    const image = eventImageUrl(event.event_image);

    return (
        <div className="flex justify-center">
            <Paper elevation={0} className="w-full max-w-2xl overflow-hidden border border-white/10 bg-black/40">
                <img src={image} alt={event.event_title} className="h-72 w-full object-cover" />
                <div className="flex flex-col gap-3 p-6">
                    <div className="flex flex-wrap items-center gap-2">
                        <Typography variant="h4" sx={{ fontWeight: 900 }}>
                            {event.event_title}
                        </Typography>
                        <EventStatusChip eventDate={event.event_date} />
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                        <CalendarMonth className="text-accent" />
                        {formatTime(event.event_date, 'dddd DD/MM/YYYY HH:mm')}
                    </div>
                    {event.event_location && (
                        <div className="flex items-center gap-2 text-gray-300">
                            <Place className="text-accent" />
                            {event.event_location}
                        </div>
                    )}
                    <div className="mt-4 flex gap-3">
                        <Button variant="contained" onClick={() => navigate(user.logged ? '/form' : '/login')}>
                            Fai una richiesta
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/')}>
                            Torna agli eventi
                        </Button>
                    </div>
                </div>
            </Paper>
        </div>
    );
}
